import { useState } from 'react';
import { Form, Button, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookmark, faSave, faCog } from '@fortawesome/free-solid-svg-icons';
import { SavePresetModal } from './SavePresetModal';
import { ManagePresetsModal } from './ManagePresetsModal';
import { DEFAULT_PRESET_ID } from './presets/defaultPreset';

const styles = {
  wrapper: {
    marginBottom: 'var(--gf-space-4, 16px)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--gf-space-2, 8px)',
    paddingBottom: 'var(--gf-space-2, 8px)',
    borderBottom: '2px solid var(--gf-primary-400, #FD7218)',
    marginBottom: 'var(--gf-space-3, 12px)',
  },
  title: {
    fontSize: 'var(--gf-text-sm, 0.875rem)',
    fontWeight: 600,
    color: 'var(--gf-secondary-700, #2D3238)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    margin: 0,
  },
  icon: {
    color: 'var(--gf-primary-400, #FD7218)',
    fontSize: 'var(--gf-text-base, 1rem)',
  },
  row: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
  },
  select: {
    flex: 1,
    minWidth: 0,
  },
  iconBtn: {
    padding: '4px 8px',
    fontSize: '0.85rem',
    lineHeight: 1,
  },
  count: {
    marginTop: 6,
    fontSize: '0.75rem',
    color: 'var(--gf-secondary-500, #6B7280)',
  },
};

export function PresetsControl({
  presets,
  activePresetId,
  setActivePresetId,
  isLoading,
  userLoggedIn,
  canSaveMore,
  userPresetCount,
  maxUserPresets,
  buildCurrentPayload,
  savePresetAsNew,
  updatePreset,
  renamePreset,
  deletePreset,
  onError,
}) {
  const [showSave, setShowSave] = useState(false);
  const [showManage, setShowManage] = useState(false);

  const activePreset = presets.find((p) => p.id === activePresetId);
  const activeIsDefault = activePresetId === DEFAULT_PRESET_ID;
  const userPresets = presets.filter((p) => !p.isBuiltIn);

  const handleDropdownChange = (e) => {
    setActivePresetId(e.target.value);
  };

  const handleSaveSubmit = async ({ mode, name }) => {
    try {
      if (mode === 'update' && !activeIsDefault) {
        const payload = buildCurrentPayload();
        await updatePreset(activePresetId, payload);
      } else {
        const payload = buildCurrentPayload();
        const newId = await savePresetAsNew(name.trim(), payload);
        setActivePresetId(newId);
      }
      setShowSave(false);
    } catch (err) {
      if (onError) onError(err);
      else console.error(err);
    }
  };

  const handleRename = async (id, newName) => {
    try {
      await renamePreset(id, newName);
    } catch (err) {
      if (onError) onError(err);
      else console.error(err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deletePreset(id);
    } catch (err) {
      if (onError) onError(err);
      else console.error(err);
    }
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.header}>
        <FontAwesomeIcon icon={faBookmark} style={styles.icon} />
        <h3 style={styles.title}>Presets</h3>
      </div>

      <div style={styles.row}>
        <Form.Select
          size="sm"
          value={activePresetId}
          onChange={handleDropdownChange}
          disabled={isLoading}
          style={styles.select}
          aria-label="Select overlay preset"
        >
          {presets.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Form.Select>

        {isLoading && <Spinner size="sm" animation="border" />}

        {userLoggedIn && !isLoading && (
          <>
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip>Save current settings as a preset</Tooltip>}
            >
              <Button
                size="sm"
                variant="outline-primary"
                style={styles.iconBtn}
                onClick={() => setShowSave(true)}
                aria-label="Save preset"
              >
                <FontAwesomeIcon icon={faSave} />
              </Button>
            </OverlayTrigger>

            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip>Manage saved presets</Tooltip>}
            >
              <Button
                size="sm"
                variant="outline-secondary"
                style={styles.iconBtn}
                onClick={() => setShowManage(true)}
                disabled={userPresets.length === 0}
                aria-label="Manage presets"
              >
                <FontAwesomeIcon icon={faCog} />
              </Button>
            </OverlayTrigger>
          </>
        )}
      </div>

      {userLoggedIn && !isLoading && (
        <div style={styles.count}>
          {userPresetCount} / {maxUserPresets} saved
        </div>
      )}

      {showSave && (
        <SavePresetModal
          show={showSave}
          onHide={() => setShowSave(false)}
          activePreset={activePreset}
          activeIsDefault={activeIsDefault}
          canSaveMore={canSaveMore}
          existingNames={userPresets.map((p) => p.name)}
          onSubmit={handleSaveSubmit}
        />
      )}

      {showManage && (
        <ManagePresetsModal
          show={showManage}
          onHide={() => setShowManage(false)}
          userPresets={userPresets}
          existingNames={userPresets.map((p) => p.name)}
          onRename={handleRename}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}
